import { Box, Typography, Button, Card, useTheme, Grid, Paper, Stack, TextField, Alert, CircularProgress } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PixIcon from '@mui/icons-material/Pix';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InputMask from 'react-input-mask';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '../../../shared/services/tenant/eventService';
import { trackPaymentMethodSelected } from '../../../shared/analytics/gtm';
import type { TrackableEvent } from '../../../shared/analytics/types';

export default function PaymentMethodPage() {
    const navigate = useNavigate();
    const { id, slug } = useParams();
    const theme = useTheme();
    const location = useLocation();
    
    // Determina o caminho base (novo com slug ou antigo com id)
    const idOrSlug = slug || id;
    const basePath = slug ? `/evento/${slug}` : `/public/event/${id}`;
    
    // Busca dados do evento para saber os métodos de pagamento disponíveis
    const { data: event, isLoading: isLoadingEvent } = useQuery({
        queryKey: ['publicEvent', idOrSlug],
        queryFn: () => eventService.getPublic(idOrSlug!).then(res => res.data),
        enabled: !!idOrSlug,
    });
    
    // Pega CPF do state ou localStorage, mas permite alteração
    const initialCpf = location.state?.cpf || localStorage.getItem('lastCpf') || '';
    const [cpf, setCpf] = useState(initialCpf);
    const [error, setError] = useState('');

    const validateCpf = (cpfValue: string) => {
        const cleanCpf = cpfValue.replace(/\D/g, '');
        if (cleanCpf.length < 11) {
            setError('CPF deve ter 11 dígitos');
            return false;
        }
        setError('');
        return true;
    };

    const handlePixPayment = () => {
        if (!validateCpf(cpf)) return;
        if (event) {
            trackPaymentMethodSelected(event as TrackableEvent, 'pix');
        }
        navigate(`${basePath}/pix`, { state: { cpf: cpf.replace(/\D/g, '') } });
    };

    const handleCardPayment = () => {
        if (!validateCpf(cpf)) return;
        if (event) {
            trackPaymentMethodSelected(event as TrackableEvent, 'card');
        }
        navigate(`${basePath}/card`, { state: { cpf: cpf.replace(/\D/g, '') } });
    };

    const handleBoletoPayment = () => {
        if (!validateCpf(cpf)) return;
        if (event) {
            trackPaymentMethodSelected(event as TrackableEvent, 'boleto');
        }
        navigate(`${basePath}/boleto`, { state: { cpf: cpf.replace(/\D/g, '') } });
    };

    const handleBack = () => {
        navigate(basePath);
    };

    // Métodos de pagamento habilitados (padrão: todos habilitados se não definido)
    const paymentPix = event?.payment_pix ?? true;
    const paymentCard = event?.payment_card ?? true;
    const paymentBoleto = event?.payment_boleto ?? true;

    // Conta quantos métodos estão disponíveis para ajustar o grid
    const availableMethods = [paymentPix, paymentCard, paymentBoleto].filter(Boolean).length;
    const gridSize = availableMethods === 1 ? 12 : availableMethods === 2 ? 6 : 4;

    if (isLoadingEvent) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, md: 4 }, background: theme.palette.background.default, minHeight: '100vh' }}>
            <Card sx={{
                background: theme.palette.background.paper,
                borderRadius: 4,
                p: 4,
                maxWidth: 700,
                margin: '32px auto',
                boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
                textAlign: 'center'
            }}>
                <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>
                    Escolha a forma de pagamento
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Confirme seu CPF e selecione como deseja pagar sua inscrição:
                </Typography>

                {/* Campo para editar CPF com máscara */}
                <Box sx={{ mb: 4 }}>
                    <InputMask
                        mask="999.999.999-99"
                        value={cpf}
                        onChange={(e) => {
                            setCpf(e.target.value);
                            if (error) setError('');
                        }}
                    >
                        {(inputProps: any) => (
                            <TextField
                                {...inputProps}
                                label="CPF"
                                placeholder="000.000.000-00"
                                sx={{ width: 250 }}
                                size="small"
                                error={!!error}
                                helperText={error}
                            />
                        )}
                    </InputMask>
                </Box>

                <Grid container spacing={3} sx={{ mt: 2 }} justifyContent="center">
                   
                    {/* Boleto/Parcelado */}
                    {paymentBoleto && (
                        <Grid item xs={12} md={gridSize}>
                            <Paper 
                                sx={{ 
                                    p: 3,
                                    height: 180,
                                    cursor: 'pointer',
                                    border: `2px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                                onClick={handleBoletoPayment}
                            >
                                <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                    <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                    <Typography variant="h6" fontWeight={600}>
                                    PAGAMENTO PARCELADO
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" textAlign="center">
                                        PIX ou Boleto até a data da viagem.
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    )}

                    {/* Cartão de Crédito */}
                    {paymentCard && (
                        <Grid item xs={12} md={gridSize}>
                            <Paper 
                                sx={{ 
                                    p: 3,
                                    height: 180,
                                    cursor: 'pointer',
                                    border: `2px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                                onClick={handleCardPayment}
                            >
                                <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                    <CreditCardIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                    <Typography variant="h6" fontWeight={600}>
                                       CARTÃO DE CRÉDITO
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" textAlign="center">
                                        Parcelamento disponível
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    )}

                    {/* PIX à vista */}
                    {paymentPix && (
                        <Grid item xs={12} md={gridSize}>
                            <Paper 
                                sx={{ 
                                    p: 3,
                                    height: 180,
                                    cursor: 'pointer',
                                    border: `2px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                                onClick={handlePixPayment}
                            >
                                <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                    <PixIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                    <Typography variant="h6" fontWeight={600}>
                                    PAGAMENTO A VISTA
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" textAlign="center">
                                        Pagamento via PIX
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                <Button
                    variant="text"
                    color="primary"
                    sx={{ mt: 4 }}
                    onClick={handleBack}
                >
                    Voltar para inscrição
                </Button>
            </Card>
        </Box>
    );
}
